import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, X, AlertTriangle, CalendarRange, Check } from 'lucide-react';

const inputCls = 'rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-action w-full';

export const isoToDisplay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

export const displayToIso = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((s || '').trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime()) || d.getDate() !== Number(dd) || d.getMonth() + 1 !== Number(mm) || d.getFullYear() !== Number(yyyy)) return null;
  return `${yyyy}-${mm}-${dd}`;
};

const EMPTY = { class_name: '', dob_from: '', dob_to: '', session: '', active: true };

export function ClassMappingManager({ classes, sessions }) {
  const [mappings, setMappings] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | mapping_id
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/class-mappings').then((r) => setMappings(r.data)).catch(() => setMappings([]));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing('new'); setForm(EMPTY); setError(null); };
  const startEdit = (m) => {
    setEditing(m.mapping_id);
    setForm({ class_name: m.class_name, dob_from: isoToDisplay(m.dob_from), dob_to: isoToDisplay(m.dob_to), session: m.session || '', active: m.active !== false });
    setError(null);
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); setError(null); };

  const validate = () => {
    if (!form.class_name) return 'Please select a class.';
    const f = displayToIso(form.dob_from);
    const t = displayToIso(form.dob_to);
    if (!f || !t) return 'Dates must be entered as DD/MM/YYYY (e.g. 01/04/2019).';
    if (f > t) return 'DOB FROM cannot be after DOB TO.';
    return null;
  };

  const save = async () => {
    const v = validate();
    if (v) { setError({ message: v }); return; }
    const payload = { class_name: form.class_name, dob_from: displayToIso(form.dob_from), dob_to: displayToIso(form.dob_to), session: form.session, active: form.active };
    setSaving(true); setError(null);
    try {
      if (editing === 'new') await api.post('/class-mappings', payload);
      else await api.put(`/class-mappings/${editing}`, payload);
      toast.success('Class mapping saved');
      cancel();
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'object' && d !== null ? d : { message: d || 'Failed to save mapping' });
    } finally { setSaving(false); }
  };

  const toggle = async (m) => {
    setError(null);
    try {
      await api.post(`/class-mappings/${m.mapping_id}/status`, { active: !m.active });
      toast.success(m.active !== false ? 'Mapping deactivated' : 'Mapping activated');
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'object' && d !== null ? d : { message: d || 'Failed to update status' });
    }
  };

  if (mappings === null) return <p className="text-sm text-[#94A3B8]">Loading…</p>;

  return (
    <div data-testid="class-mapping-manager">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-navy">DOB Range → Class mappings</p>
        {editing === null && (
          <button data-testid="add-mapping-button" onClick={startNew}
            className="flex items-center gap-1.5 rounded-lg bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-dark">
            <Plus size={14} /> Add Mapping
          </button>
        )}
      </div>

      {error && (
        <div data-testid="mapping-error" className="mb-3 flex items-start gap-2 rounded-lg border border-[#FECACA] bg-[#FEF2F2] p-3 text-xs text-[#B91C1C]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">{error.message}</p>
            {error.conflict && (
              <p data-testid="mapping-error-conflict" className="mt-1">
                Conflicts with <b>{error.conflict.class_name}</b>: {isoToDisplay(error.conflict.dob_from)} – {isoToDisplay(error.conflict.dob_to)}
                {error.conflict.session ? ` (Session ${error.conflict.session})` : ' (All sessions)'}
              </p>
            )}
          </div>
        </div>
      )}

      {editing !== null && (
        <div className="mb-4 rounded-xl border border-action/25 bg-action/5 p-4" data-testid="mapping-form">
          <p className="mb-3 text-sm font-bold text-[#172033]">{editing === 'new' ? 'New Mapping' : 'Edit Mapping'}</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#475569]">Class *</span>
              <select data-testid="mapping-class" className={inputCls} value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })}>
                <option value="">Select class</option>
                {classes.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#475569]">DOB From (DD/MM/YYYY) *</span>
              <input data-testid="mapping-dob-from" className={inputCls} placeholder="01/04/2019" value={form.dob_from} onChange={(e) => setForm({ ...form, dob_from: e.target.value })} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#475569]">DOB To (DD/MM/YYYY) *</span>
              <input data-testid="mapping-dob-to" className={inputCls} placeholder="31/03/2020" value={form.dob_to} onChange={(e) => setForm({ ...form, dob_to: e.target.value })} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#475569]">Academic Session</span>
              <select data-testid="mapping-session" className={inputCls} value={form.session} onChange={(e) => setForm({ ...form, session: e.target.value })}>
                <option value="">All sessions</option>
                {(sessions || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-[#475569]">
            <input type="checkbox" data-testid="mapping-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#002157]" />
            Active (eligible for class suggestion)
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <button data-testid="mapping-cancel" onClick={cancel} className="flex items-center gap-1.5 rounded-lg border border-[#E2E8F0] bg-white px-4 py-2 text-xs font-semibold text-[#475569] hover:bg-[#F8FAFC]"><X size={13} /> Cancel</button>
            <button data-testid="mapping-save" disabled={saving} onClick={save}
              className="flex items-center gap-1.5 rounded-lg bg-action px-4 py-2 text-xs font-semibold text-white hover:bg-action-hover disabled:opacity-60"><Check size={13} /> {saving ? 'Saving…' : 'Save Mapping'}</button>
          </div>
        </div>
      )}

      {mappings.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-4 text-xs text-[#64748B]" data-testid="mapping-empty">
          No DOB-range mappings configured yet. Until mappings are added, the legacy age-based recommendation remains in effect. Existing admission records are never altered by mapping changes.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-[#E2E8F0] hidden md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-left">
                {['Class', 'DOB From', 'DOB To', 'Session', 'Status', 'Last Updated', 'Actions'].map((h) => (
                  <th key={h} className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-navy">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.mapping_id} data-testid={`mapping-row-${m.mapping_id}`} className="border-t border-[#F1F5F9]">
                  <td className="px-3 py-2 font-semibold text-[#172033]">{m.class_name}</td>
                  <td className="px-3 py-2 text-[#475569]">{isoToDisplay(m.dob_from)}</td>
                  <td className="px-3 py-2 text-[#475569]">{isoToDisplay(m.dob_to)}</td>
                  <td className="px-3 py-2 text-[#475569]">{m.session || 'All sessions'}</td>
                  <td className="px-3 py-2">
                    <span data-testid={`mapping-status-${m.mapping_id}`} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${m.active !== false ? 'bg-[#DCFCE7] text-[#16A34A]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                      {m.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-[#94A3B8]">{m.updated_at ? new Date(m.updated_at).toLocaleDateString('en-GB') : '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button data-testid={`mapping-edit-${m.mapping_id}`} onClick={() => startEdit(m)}
                        className="flex items-center gap-1 rounded-lg border border-[#E2E8F0] px-2.5 py-1 text-xs font-semibold text-[#475569] hover:bg-[#F8FAFC]"><Pencil size={12} /> Edit</button>
                      <button data-testid={`mapping-toggle-${m.mapping_id}`} onClick={() => toggle(m)}
                        className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${m.active !== false ? 'border border-[#FECACA] text-[#DC2626] hover:bg-[#FEF2F2]' : 'border border-[#BBF7D0] text-[#16A34A] hover:bg-[#F0FDF4]'}`}>
                        {m.active !== false ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {mappings.length > 0 && (
        <div className="space-y-3 md:hidden">
          {mappings.map((m) => (
            <div key={m.mapping_id} data-testid={`mapping-card-${m.mapping_id}`} className="rounded-lg border border-[#E2E8F0] bg-white p-3.5">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-[#172033]">{m.class_name}</p>
                <span data-testid={`mapping-status-m-${m.mapping_id}`} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${m.active !== false ? 'bg-[#DCFCE7] text-[#16A34A]' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                  {m.active !== false ? 'Active' : 'Inactive'}
                </span>
              </div>
              <p className="mt-1 text-xs text-[#475569]">DOB {isoToDisplay(m.dob_from)} – {isoToDisplay(m.dob_to)}</p>
              <p className="text-xs text-[#94A3B8]">{m.session || 'All sessions'} · Updated {m.updated_at ? new Date(m.updated_at).toLocaleDateString('en-GB') : '—'}</p>
              <div className="mt-2.5 flex gap-2">
                <button data-testid={`mapping-edit-m-${m.mapping_id}`} onClick={() => startEdit(m)}
                  className="flex min-h-[36px] flex-1 items-center justify-center gap-1 rounded-lg border border-[#E2E8F0] px-2.5 py-1.5 text-xs font-semibold text-[#475569]"><Pencil size={12} /> Edit</button>
                <button data-testid={`mapping-toggle-m-${m.mapping_id}`} onClick={() => toggle(m)}
                  className={`min-h-[36px] flex-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${m.active !== false ? 'border border-[#FECACA] text-[#DC2626]' : 'border border-[#BBF7D0] text-[#16A34A]'}`}>
                  {m.active !== false ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 flex items-center gap-1.5 text-xs text-[#94A3B8]"><CalendarRange size={13} /> Both range boundaries are inclusive. Inactive mappings are kept for historical reference and never change existing admission records.</p>
    </div>
  );
}
